import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    getResidentialUnitsByProjectIdWithExtraData,
    resendResidentialUnitBuyerInvitation,
} from '@/services/residential-units';
import { getResidentialUnitGroupsByProjectId, deleteGroup } from '@/services/residential-unit-groups';
import { Trans, Plural } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import {
    LoadingIndicator,
    SlideupDialog,
    Sticky,
    StickyContainer,
    Toast,
    useIsFilterModified,
} from '@ygtechnologies/bygr-frontend-component-library';
import { ButtonBar, Checkbox, ConfirmButton, Dropdown, DropdownButton, SlideToggle } from '@ygtechnologies/rubus-ui';
import ResidentialUnitList, { ResidentialUnitViewMode } from '@/seller/components/residential-units/ResidentialUnitList';
import { ProjectPermissionToggle } from '@/components/permissions';
import {
    faEdit,
    faFileExcel,
    faPlus,
    faUser,
    faTrashAlt,
    faFileExport,
    faChevronDown,
    faSend,
    faRefresh,
    faLock,
} from '@fortawesome/pro-light-svg-icons';
import { ResidentialUnitGroup, ResidentialUnitExtraData, ChangedItem, InvitationStatus } from '@/types';
import { IState } from '@/state/IState';
import YesNoDialog from '@/components/dialogs/YesNoDialog';
import theme from './ProjectResidentialUnits.module.scss';
import { Button, Tabs } from '@ygtechnologies/rubus-ui';
import { Link } from 'react-router-dom';
import ResidentialUnitGroupEditDialog from '@/seller/components/residential-units/ResidentialUnitGroupEditDialog';
import ProjectResidentialUnitFilter from '../../residential-units/ResidentialUnitsFilter/ResidentialUnitsFilter';
import { useAsyncState, useQueryState, useParamReplace } from '@/utils/hooks';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { ResidentialUnitFilterOptions } from '@/seller/components/residential-units/ResidentialUnitsFilter';
import { useParams } from 'react-router';
import { useUnreadMessagesByUnit } from '.';
import { ResidentialUnitEdit } from '@/seller/components/residential-units/ResidentialUnitEdit';
import { NoResultsCard } from '@/components/Cards';
import { getBuyerList } from '@/services/projects';
import { orderBy } from 'lodash-es';
import { useIsMobile } from '@/utils/hooks';
import { ProjectRelativeLink } from '@/components/navigation';
import { BulkEditReadonlyModal } from './BulkEditReadonlyModal';
import classNames from 'classnames';

const soldStatusToBoolean = (status: ResidentialUnitFilterOptions['soldStatus']) => {
    switch (status) {
        case 'sold':
            return true;
        case 'unsold':
            return false;
        default:
            return null;
    }
};

type GroupHeaderProps = {
    residentialUnitGroup: ResidentialUnitGroup;
    onDelete(residentialUnitGroup: ResidentialUnitGroup): void;
    onUpdate(change: ChangedItem<ResidentialUnitGroup>): void;
    groupUnitIds: number[];
    selectedUnitIds: number[];
    onGroupSelect: (groupId: number, unitIds: number[], select: boolean) => void;
};

const GroupHeader: React.FC<GroupHeaderProps> = ({
    residentialUnitGroup,
    onDelete,
    onUpdate,
    groupUnitIds,
    selectedUnitIds,
    onGroupSelect,
}) => {
    const [showEditDialog, setShowEditDialog] = useState(false);

    const isGroupFullySelected = groupUnitIds.length > 0 && groupUnitIds.every((id) => selectedUnitIds.includes(id));

    const handleDelete = async () => {
        try {
            await deleteGroup(residentialUnitGroup.id);
            triggerHandler(onDelete, residentialUnitGroup);
        } catch (e: any) {
            if (e.status === 400) {
                Toast.error(<Trans>You can not delete a group when one or more of the units have a buyer.</Trans>);
            } else {
                throw e;
            }
        }
    };

    const handleEditSave = (updated: ResidentialUnitGroup) => {
        triggerHandler(onUpdate, new ChangedItem(residentialUnitGroup, updated));
    };

    const handleGroupSelectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onGroupSelect(residentialUnitGroup.id, groupUnitIds, e.target.checked);
    };

    return (
        <Sticky className={theme.groupHeader} stickyClassName={classNames(theme.sticky, 'stickyBleed')}>
            <div className={theme.groupHeaderLeft}>
                {residentialUnitGroup.name}
                <ProjectPermissionToggle condition="canManageResidentialUnits">
                    <Button variant="tertiary" size="xs" onClick={() => setShowEditDialog(true)} icon={faEdit}>
                        <Trans>Edit unit group</Trans>
                    </Button>

                    <ResidentialUnitGroupEditDialog
                        show={showEditDialog}
                        projectId={residentialUnitGroup.projectId}
                        residentialUnitGroupId={residentialUnitGroup.id}
                        onClose={() => setShowEditDialog(false)}
                        onSave={handleEditSave}
                    />

                    <ConfirmButton
                        onConfirm={handleDelete}
                        dialogTitle={<Trans>Delete unit group</Trans>}
                        dialogBody={
                            <Trans>
                                Are you sure you want to delete this unit group?
                                <br />
                                <p>
                                    This will delete the group,
                                    <strong> and all the residential units within it.</strong> It will not work if any of the
                                    units have a buyer.
                                </p>
                            </Trans>
                        }
                        confirmButton={{ children: <Trans>Delete</Trans> }}
                        variant="tertiary"
                        size="xs"
                        icon={faTrashAlt}>
                        <Trans>Delete</Trans>
                    </ConfirmButton>
                </ProjectPermissionToggle>
            </div>

            {
                // only if there is units in the group
                groupUnitIds.length > 0 && (
                    <div className={theme.selectGroup}>
                        <Checkbox
                            className={theme.reverseCheckbox}
                            inputSize="md"
                            checked={isGroupFullySelected}
                            onChange={handleGroupSelectChange}>
                            {isGroupFullySelected ? <Trans>Deselect group</Trans> : <Trans>Select group</Trans>}
                        </Checkbox>
                    </div>
                )
            }
        </Sticky>
    );
};

interface ResidentialUnitsGroupedProps {}

const ResidentialUnitsGrouped: React.FC<ResidentialUnitsGroupedProps> = () => {
    const project = useSelector((state: IState) => state.global.activeProject!);
    const projectId = project.id;
    const [filter, setFilter] = useQueryState<ResidentialUnitFilterOptions>(ResidentialUnitFilterOptions.empty());
    const filterModified = useIsFilterModified(filter, ResidentialUnitFilterOptions.empty);

    // Selection state
    const [selectedUnitIds, setSelectedUnitIds] = useState<number[]>([]);

    // Dialog state
    const [showSendInvitesDialog, setShowSendInvitesDialog] = useState(false);
    const [showResendInvitesDialog, setShowResendInvitesDialog] = useState(false);
    const [showBulkEditReadonlyDialog, setShowBulkEditReadonlyDialog] = useState(false);
    // const [show, setShowResendInvitesDialog] = useState(false);
    const [sendingInvites, setSendingInvites] = useState(false);
    const [resendingInvites, setResendingInvites] = useState(false);

    const isMobile = useIsMobile();

    // Invite method preferences
    const [sendInvitesViaEmail, setSendInvitesViaEmail] = useState(true);
    const [sendInvitesViaSms, setSendInvitesViaSms] = useState(false);
    const [resendInvitesViaEmail, setResendInvitesViaEmail] = useState(true);
    const [resendInvitesViaSms, setResendInvitesViaSms] = useState(false);
    const [residentialUnits, , reloadResidentialUnits] = useAsyncState<ResidentialUnitExtraData[] | null>(
        () =>
            getResidentialUnitsByProjectIdWithExtraData(projectId, {
                floors: filter.floors,
                layoutTypes: filter.layoutTypes,
                tagIds: filter.tags,
                roomIds: filter.rooms,
                groupIds: filter.groups,
                searchText: filter.searchText,
                sold: soldStatusToBoolean(filter.soldStatus),
                hasDeadline: filter.hasDeadline,
                hasUnsentOptions: filter.hasUnsentOptions !== 'all' ? filter.hasUnsentOptions === 'yes' : null,
                withBuyerStatus: filter.buyerStatus !== 'all' ? filter.buyerStatus : null,
            }),
        [filter, projectId],
        null
    );

    const [residentialUnitGroups, , reloadResidentialUnitGroups] = useAsyncState(
        () => getResidentialUnitGroupsByProjectId(projectId),
        [projectId],
        null
    );

    const sortedResidentialUnitGroups = useMemo(() => {
        if (!residentialUnitGroups) return null;
        return orderBy(residentialUnitGroups, (x) => x.name, 'asc');
    }, [residentialUnitGroups]);

    // const [viewMode, setViewMode] = useState<'compressed' | 'expanded'>('compressed');
    const [isLoading, setIsLoading] = useState(false);
    const [showCreateGroupDialog, setShowCreateGroupDialog] = useState(false);
    const [showCreateUnitDialog, setShowCreateUnitDialog] = useState(false);

    // Selection handlers
    const handleUnitSelect = useCallback((unitId: number, selected: boolean) => {
        setSelectedUnitIds((prev) => {
            if (selected) return [...prev, unitId];
            return prev.filter((id) => id !== unitId);
        });
    }, []);

    const handleGroupSelect = useCallback((groupId: number, unitIds: number[], select: boolean) => {
        setSelectedUnitIds((prev) => {
            if (select) {
                const newIds = unitIds.filter((id) => !prev.includes(id));
                return [...prev, ...newIds];
            } else {
                return prev.filter((id) => !unitIds.includes(id));
            }
        });
    }, []);

    useEffect(() => setIsLoading(true), [filter]);
    useEffect(() => setIsLoading(false), [residentialUnits]);

    const renderResidentialUnits = () => {
        if (!residentialUnits || !sortedResidentialUnitGroups) return <LoadingIndicator />;
        if (residentialUnits.length === 0 && sortedResidentialUnitGroups.length === 0) {
            return (
                <NoResultsCard>
                    <Trans>No units</Trans>
                </NoResultsCard>
            );
        }

        return (
            <div>
                <div className={theme.selectAllUnits}>
                    <Checkbox
                        inputSize="md"
                        checked={selectedUnitIds.length > 0}
                        onChange={(e) => {
                            if (e.target.checked) setSelectedUnitIds(residentialUnits.map((unit) => unit.id));
                            else setSelectedUnitIds([]);
                        }}>
                        {selectedUnitIds.length > 0 ? <Trans>Deselect all</Trans> : <Trans>Select all</Trans>}
                    </Checkbox>
                </div>
                {renderUnassignedResidentialUnits()}

                {sortedResidentialUnitGroups.map((group) => renderResidentialUnitGroup(group))}
            </div>
        );
    };

    const handleGroupDelete = () => {
        reloadResidentialUnitGroups();
        reloadResidentialUnits();
    };

    const handleGroupChange = () => reloadResidentialUnitGroups();
    const handleUnitUpdate = useCallback(() => reloadResidentialUnits(), [reloadResidentialUnits]);
    const handleDelete = useCallback(() => reloadResidentialUnits(), [reloadResidentialUnits]);

    const { viewMode } = useParams<{ viewMode: ResidentialUnitViewMode }>();
    const replaceParams = useParamReplace<{ viewMode?: ResidentialUnitViewMode }>();

    const [, getUnitThreadInfo] = useUnreadMessagesByUnit();
    const getNotificationCount = (residentialUnitId: number) => getUnitThreadInfo(residentialUnitId)?.unreadThreads ?? 0;

    const hrefGenerator = useCallback((ru) => `/residential-units/${ru.id}`, []);
    const renderUnitList = (units: ResidentialUnitExtraData[]) => {
        return (
            <ResidentialUnitList
                onDelete={handleDelete}
                onUpdate={handleUnitUpdate}
                residentialUnits={units}
                hrefGenerator={hrefGenerator}
                viewMode={viewMode}
                notificationCountGetter={getNotificationCount}
                selectedUnitIds={selectedUnitIds}
                onUnitSelect={handleUnitSelect}
            />
        );
    };

    const renderResidentialUnitGroup = (residentialUnitGroup: ResidentialUnitGroup) => {
        const relevantResidentialUnits = residentialUnits!.filter(
            (residentialUnit) => residentialUnit.residentialUnitGroupId === residentialUnitGroup.id
        );
        if (relevantResidentialUnits.length === 0 && filterModified) {
            return null;
        }

        const groupUnitIds = relevantResidentialUnits.map((unit) => unit.id);

        return (
            <StickyContainer key={residentialUnitGroup.id}>
                <div className={theme.residentialUnitGroup}>
                    <GroupHeader
                        residentialUnitGroup={residentialUnitGroup}
                        onDelete={handleGroupDelete}
                        onUpdate={handleGroupChange}
                        groupUnitIds={groupUnitIds}
                        selectedUnitIds={selectedUnitIds}
                        onGroupSelect={handleGroupSelect}
                    />

                    {renderUnitList(relevantResidentialUnits)}
                </div>
            </StickyContainer>
        );
    };

    const renderUnassignedResidentialUnits = () => {
        const relevantResidentialUnits = residentialUnits!.filter(
            (residentialUnit) => !residentialUnit.residentialUnitGroupId
        );

        if (relevantResidentialUnits.length === 0) {
            return null;
        }

        return (
            <StickyContainer>
                <div className={theme.residentialUnitGroup}>
                    <Sticky stickyClassName={classNames(theme.sticky, 'stickyBleed')} className={theme.groupHeader}>
                        <div className={theme.groupHeaderLeft}>
                            <Trans>Unassigned</Trans>
                        </div>
                        <div className={theme.selectGroup}>
                            <Checkbox
                                className={theme.reverseCheckbox}
                                inputSize="md"
                                checked={
                                    relevantResidentialUnits.length > 0 &&
                                    relevantResidentialUnits.every((unit) => selectedUnitIds.includes(unit.id))
                                }
                                onChange={(e) =>
                                    handleGroupSelect(
                                        0,
                                        relevantResidentialUnits.map((unit) => unit.id),
                                        e.target.checked
                                    )
                                }>
                                {relevantResidentialUnits.length > 0 &&
                                relevantResidentialUnits.every((unit) => selectedUnitIds.includes(unit.id)) ? (
                                    <Trans>Deselect group</Trans>
                                ) : (
                                    <Trans>Select group</Trans>
                                )}
                            </Checkbox>
                        </div>
                    </Sticky>

                    {renderUnitList(relevantResidentialUnits)}
                </div>
            </StickyContainer>
        );
    };

    const selectedUnits = useMemo(() => {
        if (!residentialUnits) return [];
        return residentialUnits.filter((unit) => selectedUnitIds.includes(unit.id));
    }, [residentialUnits, selectedUnitIds]);

    const pendingUsers = useMemo(() => {
        return selectedUnits
            .flatMap((unit) => unit.buyerInfo.users)
            .filter((user) => user.inviteStatus === InvitationStatus.Pending);
    }, [selectedUnits]);

    const invitedUsers = useMemo(() => {
        return selectedUnits
            .flatMap((unit) => unit.buyerInfo.users)
            .filter((user) => user.inviteStatus === InvitationStatus.Sent);
    }, [selectedUnits]);

    const sendInvites = async () => {
        setSendingInvites(true);
        try {
            let successCount = 0;

            for (const user of pendingUsers) {
                await resendResidentialUnitBuyerInvitation(user.inviteId, {
                    sendEmail: sendInvitesViaEmail && !!user.email,
                    sendSms: sendInvitesViaSms && !!user.phoneNumber,
                });
                successCount++;
            }

            const methods: string[] = [];
            if (sendInvitesViaEmail) methods.push('email');
            if (sendInvitesViaSms) methods.push('SMS');
            const methodText = methods.length > 1 ? methods.join(' and ') : methods[0];

            Toast.success(
                <Plural
                    one={`Invitation has been sent via ${methodText}.`}
                    other={`${successCount} invitations have been sent via ${methodText}.`}
                    value={successCount}
                />
            );

            // Refresh data to show updated invitation status
            reloadResidentialUnits();
        } catch (error: any) {
            console.error('Failed to send invites:', error);
            Toast.error(<Trans>Failed to send invitations. Please try again.</Trans>);
        } finally {
            setSendingInvites(false);
            setShowSendInvitesDialog(false);
            setSelectedUnitIds([]);
        }
    };

    const resendInviteToInvitedBuyers = async () => {
        setResendingInvites(true);
        try {
            let successCount = 0;

            for (const user of invitedUsers) {
                await resendResidentialUnitBuyerInvitation(user.inviteId, {
                    sendEmail: resendInvitesViaEmail && !!user.email,
                    sendSms: resendInvitesViaSms && !!user.phoneNumber,
                });
                successCount++;
            }

            const methods: string[] = [];
            if (resendInvitesViaEmail) methods.push(t`email`);
            if (resendInvitesViaSms) methods.push(t`SMS`);
            const methodText = methods.length > 1 ? methods.join(t` and `) : methods[0];

            Toast.success(
                <Plural
                    one={`Invitation has been resent via ${methodText}.`}
                    other={`${successCount} invitations have been resent via ${methodText}.`}
                    value={successCount}
                />
            );

            // Refresh data to show updated invitation status
            reloadResidentialUnits();
        } catch (error: any) {
            console.error('Failed to resend invitations:', error);
            Toast.error(<Trans>Failed to resend invitations. Please try again.</Trans>);
        } finally {
            setResendingInvites(false);
            setShowResendInvitesDialog(false);
            setSelectedUnitIds([]);
        }
    };

    return (
        <div className={selectedUnitIds.length > 0 ? theme.contentWithSlideup : ''}>
            <ButtonBar sticky stickyClassName="stickyBleed">
                <Tabs activeId={viewMode} variant="pill" size="sm">
                    <Tabs.Tab
                        id={ResidentialUnitViewMode.Compressed}
                        as={Link}
                        to={replaceParams({ viewMode: ResidentialUnitViewMode.Compressed })}
                        label={<Trans>Compressed view</Trans>}
                    />
                    <Tabs.Tab
                        id={ResidentialUnitViewMode.Expanded}
                        as={Link}
                        to={replaceParams({ viewMode: ResidentialUnitViewMode.Expanded })}
                        label={<Trans>Expanded view</Trans>}
                    />
                </Tabs>

                <ButtonBar.Right>
                    <ProjectPermissionToggle condition="canManageResidentialUnits">
                        <DropdownButton variant="tertiary" icon={faChevronDown} label={<Trans>Import</Trans>}>
                            <Dropdown.Item as={ProjectRelativeLink} icon={faFileExcel} to={`/residential-units/import`}>
                                <Trans>Import units</Trans>
                            </Dropdown.Item>
                            <ProjectPermissionToggle condition="canInviteBuyers">
                                <Dropdown.Item as={ProjectRelativeLink} to={`/invite-buyers`} icon={faUser}>
                                    <Trans>Invite buyers</Trans>
                                </Dropdown.Item>
                            </ProjectPermissionToggle>
                            <Dropdown.Item as="a" href={getBuyerList(projectId)} icon={faFileExport}>
                                <Trans>Export buyers</Trans>
                            </Dropdown.Item>
                        </DropdownButton>

                        <Button variant="tertiary" onClick={() => setShowCreateGroupDialog(true)} icon={faPlus}>
                            <Trans id="create_new_residential_unit_group">Create new group</Trans>
                        </Button>

                        <Button variant="tertiary" icon={faPlus} onClick={() => setShowCreateUnitDialog(true)}>
                            <Trans>Create new unit</Trans>
                        </Button>

                        <ResidentialUnitGroupEditDialog
                            show={showCreateGroupDialog}
                            projectId={projectId}
                            onClose={() => setShowCreateGroupDialog(false)}
                            onSave={handleGroupChange}
                        />
                        <ResidentialUnitEdit
                            show={showCreateUnitDialog}
                            toggleOpen={() => setShowCreateUnitDialog(false)}
                            residentialUnitId={null}
                            onSave={handleUnitUpdate}
                        />
                    </ProjectPermissionToggle>
                </ButtonBar.Right>
                <ProjectResidentialUnitFilter isLoading={isLoading} filter={filter} onFilterChange={setFilter} />
            </ButtonBar>

            {sendingInvites || resendingInvites ? (
                <div className={theme.loadingIndicator}>
                    <LoadingIndicator />

                    <p>
                        <Trans>Sending invitations, please wait...</Trans>
                    </p>
                </div>
            ) : (
                renderResidentialUnits()
            )}

            <SlideupDialog
                className={'fix-slide-up-position'}
                onCloseDialog={() => setSelectedUnitIds([])}
                isOpen={selectedUnitIds.length > 0}
                heading={<Plural one="# unit selected" other="# units selected" value={selectedUnitIds.length} />}>
                <div className={theme.slideUpBody}>
                    {isMobile ? (
                        <DropdownButton
                            variant="tertiary"
                            icon={faChevronDown}
                            label={<span className={theme.dropDownMainText}>{<Trans>Select action</Trans>}</span>}>
                            <Dropdown.Item icon={faSend} onClick={() => setShowSendInvitesDialog(true)}>
                                <Trans>Send invitations</Trans>
                            </Dropdown.Item>

                            <Dropdown.Item icon={faRefresh} onClick={() => setShowResendInvitesDialog(true)}>
                                <Trans>Resend invitations</Trans>
                            </Dropdown.Item>

                            <ProjectPermissionToggle condition="canManageResidentialUnits">
                                <Dropdown.Item icon={faLock} onClick={() => setShowBulkEditReadonlyDialog(true)}>
                                    <Trans>Edit Readonly</Trans>
                                </Dropdown.Item>
                            </ProjectPermissionToggle>
                        </DropdownButton>
                    ) : (
                        <>
                            <ProjectPermissionToggle condition="canManageResidentialUnits">
                                <Button
                                    icon={faLock}
                                    color="tertiary"
                                    onClick={() => setShowBulkEditReadonlyDialog(true)}
                                    disabled={sendingInvites || resendingInvites}>
                                    <Trans>Edit Readonly</Trans>
                                </Button>
                            </ProjectPermissionToggle>

                            <Button
                                icon={faSend}
                                color="tertiary"
                                onClick={() => setShowSendInvitesDialog(true)}
                                disabled={pendingUsers.length === 0 || sendingInvites || resendingInvites}
                                loading={sendingInvites}>
                                <Trans>Send invites</Trans>
                            </Button>

                            <Button
                                icon={faRefresh}
                                color="tertiary"
                                onClick={() => setShowResendInvitesDialog(true)}
                                disabled={invitedUsers.length === 0 || sendingInvites || resendingInvites}
                                loading={resendingInvites}>
                                <Trans>Resend invitations</Trans>
                            </Button>
                        </>
                    )}
                </div>
            </SlideupDialog>

            <YesNoDialog
                title={<Trans>Send Invites</Trans>}
                body={
                    <div>
                        <p>
                            <Trans>Are you sure you want to send invites to {pendingUsers.length} user(s)?</Trans>
                        </p>
                        <p>
                            <strong>
                                <Trans>Note:</Trans>
                            </strong>{' '}
                            <Trans>
                                Only users that have been added, but have not received an invitation will be affected.
                            </Trans>
                        </p>

                        {!sendingInvites && (
                            <div className={theme.inviteMethodSelection}>
                                <h4>
                                    <Trans>Send invitations via:</Trans>
                                </h4>
                                <div className={theme.toggleOptions}>
                                    <div className={theme.toggleOption}>
                                        <SlideToggle
                                            checked={sendInvitesViaEmail}
                                            onChange={(e) => setSendInvitesViaEmail(e.target.checked)}
                                            disabled={true}>
                                            <Trans>Email (required)</Trans>
                                        </SlideToggle>
                                    </div>
                                    <div className={theme.toggleOption}>
                                        <SlideToggle
                                            checked={sendInvitesViaSms}
                                            onChange={(e) => setSendInvitesViaSms(e.target.checked)}
                                            disabled={sendingInvites}>
                                            <Trans>SMS</Trans>
                                        </SlideToggle>
                                    </div>
                                </div>
                            </div>
                        )}

                        {sendingInvites && <LoadingIndicator />}
                    </div>
                }
                show={showSendInvitesDialog}
                onAccept={sendInvites}
                onClose={() => !sendingInvites && setShowSendInvitesDialog(false)}
                confirmButtonText={sendingInvites ? <Trans>Sending...</Trans> : <Trans>Send Invites</Trans>}
                cancelButtonText={<Trans>Cancel</Trans>}
            />

            <YesNoDialog
                title={<Trans>Resend Invites</Trans>}
                body={
                    <div>
                        <p>
                            <Trans>Are you sure you want to resend invitations to {invitedUsers.length} user(s)?</Trans>
                        </p>
                        <p>
                            <strong>
                                <Trans>Note:</Trans>
                            </strong>{' '}
                            <Trans>Only users that have already received an invitation will be affected.</Trans>
                        </p>

                        {!resendingInvites && (
                            <div className={theme.inviteMethodSelection}>
                                <h4>
                                    <Trans>Resend invitations via:</Trans>
                                </h4>
                                <div className={theme.toggleOptions}>
                                    <div className={theme.toggleOption}>
                                        <SlideToggle
                                            checked={resendInvitesViaEmail}
                                            onChange={(e) => setResendInvitesViaEmail(e.target.checked)}
                                            disabled={resendingInvites}>
                                            <Trans>Email</Trans>
                                        </SlideToggle>
                                    </div>
                                    <div className={theme.toggleOption}>
                                        <SlideToggle
                                            checked={resendInvitesViaSms}
                                            onChange={(e) => setResendInvitesViaSms(e.target.checked)}
                                            disabled={resendingInvites}>
                                            <Trans>SMS</Trans>
                                        </SlideToggle>
                                    </div>
                                </div>
                                {!resendInvitesViaEmail && (
                                    <div className={theme.warningMessage}>
                                        <Trans>Email is required for resending invitations.</Trans>
                                    </div>
                                )}
                            </div>
                        )}

                        {resendingInvites && (
                            <div style={{ marginTop: '16px', textAlign: 'center' }}>
                                <LoadingIndicator />
                                <p>
                                    <Trans>Resending invitations...</Trans>
                                </p>
                            </div>
                        )}
                    </div>
                }
                show={showResendInvitesDialog}
                onAccept={() => {
                    if (resendInvitesViaEmail) {
                        resendInviteToInvitedBuyers();
                    }
                }}
                onClose={() => !resendingInvites && setShowResendInvitesDialog(false)}
                confirmButtonText={resendingInvites ? <Trans>Resending...</Trans> : <Trans>Resend Invites</Trans>}
                cancelButtonText={<Trans>Cancel</Trans>}
                disableAccept={!resendInvitesViaEmail}
            />

            <BulkEditReadonlyModal
                isOpen={showBulkEditReadonlyDialog}
                residentialUnits={selectedUnits}
                onClose={() => setShowBulkEditReadonlyDialog(false)}
                onSuccess={() => {
                    reloadResidentialUnits();
                    setSelectedUnitIds([]);
                }}
            />
        </div>
    );
};
export default ResidentialUnitsGrouped;
